import { CCPPricing, PricingPlanType } from '@atlassian/cssxp-typescommon';
import type { ProductOffering, ProductAddOn } from '@css-xp/graphtypes/src/generated-types-sxp';
import { Benefit, Currency } from '@css-xp/graphtypes/src/generated-types-new';
import { AppPromoCodeDetails } from '@css-xp/ppc-checkout';
import type {
  CartLinkedEntitlementsSchema,
  CartProductSchema,
  BillEstimateResponse,
  ValidationErrorSchema,
  BillToInfo,
  CollectionToInstanceOfferingMapResponse,
} from '../types';
import { FeatureFlags, NativeProductResponse } from '@css-xp/ppc-purchase-experience';
import { OmsCapability } from './utils';

export type OfferingAndPricingPlan = Pick<
  ProductOffering,
  'id' | 'offeringName' | 'pricingType' | 'level' | 'trialLengthInDays' | 'offeringGroup'
> & {
  defaultPricingPlan?: {
    id?: string;
  };
  defaultPricingPlansEligibilityMap?: Record<string, string>;
  pricingPlans: {
    id: string;
    currency: string;
    type: string;
  }[];
};

export type StatuspageAddonsOfferingsMap = Pick<ProductAddOn, 'name'> & {
  offeringId: string;
};

export type AppPromotionResponse = AppPromoCodeDetails;
/**
 * Purpose of storing CurrentScreenTypeEnum is to identify the current screen.
 * Drawer for order summary and order confirmation screen are same with the respective content.
 * In order to have different functionality of `Back` button for each case, we need to know the current screen.
 */
export enum CurrentScreenTypeEnum {
  ORDER_PAGE = 'ORDER_PAGE',
  ORDER_SUMMARY = 'ORDER SUMMARY',
  ORDER_CONFIRMATION = 'ORDER CONFIRMATION',
}

export enum EnrichmentStatus {
  PENDING = 'PENDING',
  ERROR = 'ERROR',
}

export type State = {
  sidebarFilter?: {
    searchQuery: string;
    billingProfileIds: string[];
  };
  isLoading: boolean;
  isRefreshEstimateLoading?: boolean;
  products: Record<CartProductSchema['id'], CartProductSchema>;
  linkedEntitlements: Record<CartLinkedEntitlementsSchema['containerOrProductId'], CartLinkedEntitlementsSchema[]>;
  addons: Record<CartLinkedEntitlementsSchema['containerOrProductId'], CartLinkedEntitlementsSchema[]>;
  inactiveAddons: Record<CartLinkedEntitlementsSchema['containerOrProductId'], CartLinkedEntitlementsSchema[]>;
  sandboxProducts: Record<CartProductSchema['id'], CartProductSchema>;
  inactiveProducts: Record<CartProductSchema['id'], CartProductSchema>;
  sandboxApps: Record<CartLinkedEntitlementsSchema['containerOrProductId'], CartLinkedEntitlementsSchema[]>;
  inactiveApps: Record<CartLinkedEntitlementsSchema['containerOrProductId'], CartLinkedEntitlementsSchema[]>;
  selectedProducts: Record<CartProductSchema['id'], Partial<CartProductSchema>>;
  selectedLinkedEntitlements?: Record<CartLinkedEntitlementsSchema['id'], Partial<CartLinkedEntitlementsSchema>>;
  offeringsAndpricingPlans?: Record<CartProductSchema['productId'], OfferingAndPricingPlan[]>;
  statuspageAddonsOfferingsMap?: Record<string, StatuspageAddonsOfferingsMap[]>;
  nativeProduct?: NativeProductResponse;
  partnerPromotions: Record<
    CartProductSchema['productId'] | CartLinkedEntitlementsSchema['productId'],
    string | undefined
  >;
  selectedPartnerAccountData?: {
    discountTier: string;
    programLevelGlobal: string;
    transactionAccountId: string;
    name: string;
    taxId: string;
    billTo: BillToInfo;
    partnerType: string;
  };
  billEstimate?: BillEstimateResponse | null;
  recurringBillEstimate?: BillEstimateResponse | null;
  hasProductMetadataFetchFailed?: boolean;
  hasNativeProductWithPricingPlanFetchFailed?: boolean;
  validationErrors?: Record<CartProductSchema['id'], ValidationErrorSchema> | null;
  invoiceGroupId: string;
  invoiceGroupName?: string;
  nextBillDate: {
    cartLevel?: string;
    cloudSiteLevel?: Record<CartProductSchema['cloudSite'], string>;
    entitlementLevel?: Record<string, string>;
  };
  cartSearch?: {
    query?: string;
    statuses?: CartProductSchema['productStatuses'];
  };
  currentScreen?: CurrentScreenTypeEnum;
  // this is to track if invoice-group is auto selected or manually selected on cart screen
  manualSelectedInvoiceGroupId: string;
  featureFlags?: FeatureFlags;
  billingProfiles?: {
    id: string;
    name: string;
    shipToPriceEligibility: CCPPricing;
    currency: `${Currency}`;
  }[];
  enrichmentStatus?: Record<CartProductSchema['id'] | CartLinkedEntitlementsSchema['id'], EnrichmentStatus>;
  selectedCustomer?: { name: string; ids: string[] } | null;
  estimatedUsersForRecurringBillEstimate?: Record<CartProductSchema['id'], string>;
  partnerPromotionBenefits: Record<string, Benefit[]>;
  sandboxParentData?: Record<string, Record<string, string>>;
  // Below is to store the collection offering ID to instances offering Id map
  collectionToInstanceOfferingMap?: CollectionToInstanceOfferingMapResponse;
};

export type StoreProduct = State['products'][keyof State['products']];

export type GetOmsPayload = {
  initialStoreProducts?: State['products'];
  offeringsAndpricingPlans?: State['offeringsAndpricingPlans'];
  cartProducts: CartProductSchema[];
  cartApps: State['linkedEntitlements'];
  nextBillDate: State['nextBillDate'];
  selectedProducts: State['selectedProducts'];
  transactionAccountId: string;
  invoiceGroupId: string;
  partnerPromotions: State['partnerPromotions'];
  omsCapability: OmsCapability;
  endCustomerContacts?: string[];
  productsArr: CartProductSchema[];
  selectedLinkedEntitlements: State['selectedLinkedEntitlements'];
  isMultipleIgChangeHandlingForOrderEnabled?: boolean;
  currentPricingEligibility?: `${PricingPlanType}`;
  isIGChangeForFreeEntitlementEnabled?: boolean;
  isECCInMetaDataDisabled?: boolean;
  shouldExcludeDiscretionaryDiscount?: boolean;
  sandboxProducts: State['sandboxProducts'];
  sandboxApps: State['sandboxApps'];
  currentCurrency?: `${Currency}`;
  isNonCommercialPricingEnabled?: boolean;
  isJPDAsJiraFamilyEnabled?: boolean;
  isCoTermInUofEnabled?: boolean;
  isSandboxAppCreateInUOFEnabled?: boolean;
  sandboxParentData?: State['sandboxParentData'];
  isExcludeExpiredPromotionEnabled?: boolean;
  isAppEditionSupportEnabled?: boolean;
};

export type PartnerPromotionMap = {
  productIdPartnerPromotionIdMap: State['partnerPromotions'];
  promotionIdToBenefitsMap: State['partnerPromotionBenefits'];
};

export type { FeatureFlags };
